import Big from 'big.js';
import {combineLatest, merge, Observable} from 'rxjs';
import {filter, map, scan, switchMap} from 'rxjs/operators';
import {QtyType} from '@/enum/order';
import {timeUtil} from '@/helper/time';
import {numUtil} from '@/helper/num';
import {tradePair$} from '@/state/trade-pair';
import {contractQtyType$} from '@/state/book';
import {tradeMsg$} from '@/raw/trades';
import type {ws, ReduceAction, TradePair, TradeData, TradeItem, ContractPair, SpotPair} from '@/types';

type TradeMsg = ws.TradeMsg;
type TradeItemRaw = ws.TradeItemRaw;
type TradeItemRawV2 = ws.TradeItemRawV2;

type Action = ReduceAction<TradeItemRaw[] | TradeItemRawV2[]>;

export const trades$ = createTradeData$(tradePair$, tradeMsg$, contractQtyType$);

const MAX_LENGTH = 200;
/**
 * 时间窗口，该时段内的连续的'涨'或连续的'跌'会忽略
 * @type {number}
 */
const TIME_WINDOW = 450;

function createTradeData$(
  tradePair$: Observable<TradePair>,
  tradeMsg$: Observable<TradeMsg>,
  contractQtyType$
): Observable<TradeData> {
  const reset$ = tradePair$.pipe(
    map(({symbol, type}: TradePair): TradeData => ({reset: true, symbol, tradeType: type, trades: []}))
  );
  const newTrades$ = tradePair$.pipe(
    switchMap((tradePair: TradePair) => convertNewTrades$(tradePair, tradeMsg$, contractQtyType$))
  );
  return merge<TradeData[]>(reset$, newTrades$);
}

function convertTrade(
  [t, side, priceEp, size]: TradeItemRaw,
  fa: boolean,
  fb: boolean,
  calcPrice: (priceEp: number) => string,
  calcSizeStr: (size: number) => string,
  tradePair,
  contractQtyType: string
): TradeItem {
  const calcValueStr = calcSizeValue(size, priceEp, tradePair, contractQtyType);
  const calcValue = calcSize(size, priceEp, tradePair, contractQtyType);
  return {
    time: timeUtil.formatTime(t / 1e6),
    buy: side === 'Buy',
    sell: side === 'Sell',
    priceEp,
    price: calcPrice(priceEp),
    size: tradePair.type === 'Spot' ? size : calcValue,
    sizeStr: tradePair.type === 'Spot' ? calcSizeStr(size) : calcValueStr,
    up: false,
    down: false,
    fa,
    fb,
  };
}

function updateUpAndDown(trades: TradeItem[]) {
  const len = trades.length;
  if (len < 2) {
    return;
  }

  for (let i = len - 1; i > 0; i--) {
    const lastTrade = trades[i];
    const trade = trades[i - 1];
    trade.up = trade.priceEp > lastTrade.priceEp;
    trade.down = trade.priceEp < lastTrade.priceEp;
  }
}

function calcNext(old: number, isFlashing: boolean): number {
  if (isFlashing) {
    return old;
  }
  return old === 2 ? 1 : 2;
}

function updateCaches(trades: TradeItemRaw[], flash: Map<number, number>, times: Map<number, number>, now: number) {
  trades.forEach((_, index) => {
    const lastTime = times.get(index) || 0;
    const current = flash.get(index);
    const next = calcNext(current, TIME_WINDOW > now - lastTime);
    if (next !== current) {
      flash.set(index, next);
      times.set(index, now);
    }
  });
}

function convertNewTrades$(tradePair: TradePair, tradeMsg$: Observable<TradeMsg>, contractQtyType$) {
  const flash = new Map<number, number>();
  const times = new Map<number, number>();

  const snapshotTradesAction$ = tradeMsg$.pipe(
    tradesFilter(tradePair.symbol, 'snapshot'),
    map(
      (tradeMsg: TradeMsg): Action =>
        function () {
          return tradeMsg.trades;
        }
    )
  );

  const incrementalTradesAction$ = tradeMsg$.pipe(
    tradesFilter(tradePair.symbol, 'incremental'),
    map((tradeMsg: TradeMsg): Action => {
      updateCaches(tradeMsg.trades, flash, times, Date.now());
      return function (oldTrades) {
        // @ts-ignore
        return oldTrades ? tradeMsg.trades.concat(oldTrades) : tradeMsg.trades;
      };
    })
  );

  const truncatedTrades$ = merge<Action[]>(snapshotTradesAction$, incrementalTradesAction$).pipe(
    scan<Action, TradeItemRaw[] | TradeItemRawV2[]>((p, c) => c(p).slice(0, MAX_LENGTH), null)
  );

  const calcPrice = calcPriceFun(tradePair);
  const calcSizeStr = calcSizeStrFun(tradePair);

  return combineLatest([truncatedTrades$, contractQtyType$]).pipe(
    map(([truncatedTrades, contractQtyType]: any[]) => {
      const result = truncatedTrades
        .filter((trade) => (tradePair.timeline?.length > 0 ? trade[0] / 1e6 > (tradePair?.timeline[1] || 0) : true))
        .map((trade, index) => {
          const fab = flash.get(index);
          return convertTrade(trade, fab === 1, fab === 2, calcPrice, calcSizeStr, tradePair, contractQtyType);
        });
      updateUpAndDown(result);
      return {reset: false, symbol: tradePair.symbol, tradeType: tradePair.type, trades: result};
    })
  );
}

function calcPriceFun(tradePair: TradePair) {
  if (tradePair.type === 'Spot') {
    const {pricePrecision, quoteQtyScale} = tradePair as SpotPair;
    return function (priceEp: number) {
      return numUtil.delimit(numUtil.floor(priceEp, pricePrecision, quoteQtyScale));
    };
  }
  if (['Perpetual', 'PerpetualV2'].indexOf(tradePair.type) > -1) {
    const {pricePrecision, priceScale} = tradePair;
    return function (priceEp: number) {
      return numUtil.delimit(numUtil.floor(priceEp, pricePrecision, priceScale));
    };
  }
  return null;
}

function calcSizeStrFun(tradePair: TradePair) {
  if (tradePair.type === 'Spot') {
    const {baseQtyPrecision, baseQtyScale} = tradePair as SpotPair;
    return function (size: number) {
      return numUtil.abbreviateNumber(numUtil.floor(size, baseQtyPrecision, baseQtyScale), baseQtyPrecision);
    };
  }
  if (['Perpetual', 'PerpetualV2'].indexOf(tradePair.type) > -1) {
    return function (size: number) {
      return numUtil.delimit(size);
    };
  }
  return null;
}

function tradesFilter(symbol: string, type: string) {
  return filter((tradeMsg: TradeMsg) => tradeMsg.symbol === symbol && tradeMsg.type === type);
}

function calcSizeValue(size: number, priceEp: number, tradePair: ContractPair, qtyType: string): string {
  const {contractSide, contractSize, valuePrecision, contractSizePrecision, qtyPrecision = 0, priceFactor} = tradePair;
  if (!contractSize) return '0';

  if (qtyType === QtyType.Cont) {
    const precision = size > 1e3 ? 2 : qtyPrecision;
    return numUtil.abbreviateNumber(size, precision);
  }
  if (qtyType === QtyType.Base) {
    const value = calcValue(size, contractSide, contractSize, priceEp, priceFactor);
    return numUtil.abbreviateNumber(value, valuePrecision);
  }
  if (qtyType === QtyType.Coin) {
    const value = Big(size).times(contractSize);
    const precision = Number(value) > 1e3 ? 2 : contractSizePrecision;
    return numUtil.abbreviateNumber(value, precision);
  }
  return '0';
}

function calcSize(size: number, priceEp: number, tradePair: ContractPair, qtyType: string): number {
  const {contractSide, contractSize, priceFactor} = tradePair;
  if (!contractSize) return 0;

  if (qtyType === QtyType.Cont) {
    return size;
  }
  if (qtyType === QtyType.Base) {
    return calcValue(size, contractSide, contractSize, priceEp, priceFactor);
  }
  if (qtyType === QtyType.Coin) {
    return Number(Big(size).times(contractSize));
  }
  return 0;
}

function calcValue(
  size: number,
  contractSide: number,
  contractSize: number,
  priceEp: number,
  priceFactor: number
): number {
  if (!contractSize) return 0;

  const sizeBig = Big(size).times(contractSize);
  return Number(contractSide > 0 ? sizeBig.times(priceEp).div(priceFactor) : sizeBig.times(priceFactor).div(priceEp));
}
